// In-game paper map (M key) + settlement-name toasts (M11). The map renders
// the same relief the /map.html viewer shows — hillshade + hypsometric tint +
// water + real road paths + settlement names — once into an offscreen canvas,
// then draws a player marker while open. Entering a settlement footprint pops
// a "ПРИМОРСК — Primorsk" toast.

// The one window-level keydown handler that costs a CHARACTER rather than just
// being rude: it preventDefault()s KeyM, and preventDefault on keydown
// suppresses text insertion whatever propagation stage calls it. So "Mikkel"
// typed into the join screen's name field came out "ikkel" and every 'm' also
// toggled this overlay behind the dialog — with an odd number of them the
// player pressed PLAY and spawned looking at a full-screen dark map, pointer
// lock already taken, with nothing on screen saying M clears it. input.js's own
// gate cannot help here; it only guards its own listener, which is exactly why
// keyEventIsForGame() is exported.
import { keyEventIsForGame } from '../player/input';

const BASE = 'assets/map10k/';

const hex = (c) => [parseInt(c.slice(1, 3), 16), parseInt(c.slice(3, 5), 16), parseInt(c.slice(5, 7), 16)];
const RAMP = [[0, '#54743f'], [50, '#6f8449'], [130, '#8f8756'], [240, '#8c8265'],
  [360, '#8b8778'], [470, '#a8a69f'], [560, '#e6e6e4']].map(([h, c]) => [h, hex(c)]);
function rampColor(h) {
  if (h <= RAMP[0][0]) return RAMP[0][1];
  for (let i = 1; i < RAMP.length; i++) {
    if (h <= RAMP[i][0]) {
      const [h0, c0] = RAMP[i - 1], [h1, c1] = RAMP[i];
      const t = (h - h0) / (h1 - h0);
      return [0, 1, 2].map((k) => c0[k] + (c1[k] - c0[k]) * t);
    }
  }
  return RAMP[RAMP.length - 1][1];
}

export class MapOverlay {
  constructor(terrain, roads) {
    this.terrain = terrain;
    this.roads = roads;
    this.visible = false;
    this.lastSettlement = null;
    this.toastAcc = 0;

    this.wrap = document.createElement('div');
    this.wrap.style.cssText = 'position:fixed;inset:0;background:#0a0d10d9;display:none;'
      + 'z-index:30;justify-content:center;align-items:center';
    this.canvas = document.createElement('canvas');
    this.canvas.style.cssText = 'max-width:92vmin;max-height:92vmin;border:2px solid #2c3542;border-radius:4px';
    this.wrap.appendChild(this.canvas);
    document.body.appendChild(this.wrap);

    this.toast = document.createElement('div');
    this.toast.style.cssText = 'position:fixed;top:14vh;left:50%;transform:translateX(-50%);'
      + 'color:#e8e4d8;font:600 26px/1.3 system-ui,sans-serif;text-align:center;'
      + 'text-shadow:0 2px 8px #000;opacity:0;transition:opacity 1.2s;z-index:25;pointer-events:none';
    document.body.appendChild(this.toast);

    window.addEventListener('keydown', (e) => {
      // a text field or a modal layer owns the keyboard: M is a letter there,
      // not a map toggle, and buildBase() must not fetch water.bin and
      // rasterise the whole relief in the middle of name entry
      if (!keyEventIsForGame(e)) return;
      if (e.code === 'KeyM') {
        e.preventDefault();
        this.toggle();
      }
    });
  }

  async init() {
    this.stl = await (await fetch(`${BASE}catalog/settlements.json`)).json();
    return this;
  }

  async buildBase() {
    const t = this.terrain;
    const res = t.pvRes;
    let water = null;
    try { water = new Uint8Array(await (await fetch(`${BASE}water.bin`)).arrayBuffer()); } catch { }
    this.base = document.createElement('canvas');
    this.base.width = this.base.height = res;
    const ctx = this.base.getContext('2d');
    const img = ctx.createImageData(res, res);
    const d = img.data;
    const cell = t.H.mapSize / (res - 1);
    const h = t.preview;
    const SEA = hex('#2e4d63'), DEEP = hex('#1d3a52'), RIV = [61, 111, 143];
    for (let iy = 0; iy < res; iy++) {
      for (let ix = 0; ix < res; ix++) {
        const i = iy * res + ix;
        let r, g, b;
        const hh = h[i];
        if (hh <= 0) {
          const k = Math.min(1, -hh / 45);
          r = SEA[0] + (DEEP[0] - SEA[0]) * k;
          g = SEA[1] + (DEEP[1] - SEA[1]) * k;
          b = SEA[2] + (DEEP[2] - SEA[2]) * k;
        } else if (water && water[i] >= 2) {
          [r, g, b] = RIV;
        } else {
          // cheap hillshade from the height preview
          const x1 = Math.min(res - 1, ix + 1), y1 = Math.min(res - 1, iy + 1);
          const dhx = (h[iy * res + x1] - hh) / cell;
          const dhy = (h[y1 * res + ix] - hh) / cell;
          const len = Math.sqrt(dhx * dhx + dhy * dhy + 1);
          const sh = Math.max(0, (-dhx * -0.5 + -dhy * 0.5 + 0.707) / len);
          const c = rampColor(hh);
          const s = 0.45 + 0.75 * sh;
          r = c[0] * s; g = c[1] * s; b = c[2] * s;
        }
        const o = i * 4;
        d[o] = r; d[o + 1] = g; d[o + 2] = b; d[o + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);

    // roads + names on top
    const half = t.half;
    const toPx = (x, z) => [((x + half) / t.H.mapSize) * res, ((half - z) / t.H.mapSize) * res];
    const STYLE = { highway: ['#e8a850', 2.2], main: ['#e0e0e0', 1.6], village: ['#b0b0b0', 1],
      dirt: ['#a07850', 0.8], rail: ['#2a2a2a', 1.4] };
    for (const r of this.roads?.data?.roads ?? []) {
      const [c, w] = STYLE[r.class] || STYLE.village;
      ctx.strokeStyle = c;
      ctx.lineWidth = w;
      ctx.globalAlpha = 0.8;
      if (r.class === 'rail') ctx.setLineDash([6, 3]);
      ctx.beginPath();
      r.pts.forEach(([x, z], i) => {
        const [px, py] = toPx(x, z);
        i ? ctx.lineTo(px, py) : ctx.moveTo(px, py);
      });
      ctx.stroke();
      ctx.setLineDash([]);
    }
    ctx.globalAlpha = 1;
    ctx.font = '600 13px system-ui, sans-serif';
    for (const e of this.stl.entries) {
      if (!['city', 'town', 'small_town'].includes(e.kind)) continue;
      const [px, py] = toPx(e.pos[0], e.pos[1]);
      ctx.fillStyle = '#ffd27a';
      ctx.beginPath();
      ctx.arc(px, py, e.kind === 'city' ? 5 : 3.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#f2ead8';
      ctx.strokeStyle = '#000c';
      ctx.lineWidth = 3;
      ctx.strokeText(e.nameLat, px + 7, py - 4);
      ctx.fillText(e.nameLat, px + 7, py - 4);
    }
    this.canvas.width = this.canvas.height = res;
  }

  async toggle() {
    this.visible = !this.visible;
    this.wrap.style.display = this.visible ? 'flex' : 'none';
    if (this.visible && !this.base) await this.buildBase();
  }

  update(dt, pos, heading) {
    // settlement toast (checked ~1/s)
    this.toastAcc += dt;
    if (this.toastAcc > 1 && this.stl) {
      this.toastAcc = 0;
      let cur = null;
      for (const e of this.stl.entries) {
        if (!e.footprint || e.kind === 'landmark') continue;
        if (Math.abs(pos.x - e.pos[0]) < e.footprint[0] / 2 + 40
          && Math.abs(pos.z - e.pos[1]) < e.footprint[1] / 2 + 40) { cur = e; break; }
      }
      if (cur && cur.id !== this.lastSettlement) {
        this.lastSettlement = cur.id;
        this.toast.innerHTML = `${cur.nameCyr.toUpperCase()}<br><span style="font-size:16px;color:#b8b0a0">${cur.nameLat}</span>`;
        this.toast.style.opacity = 1;
        clearTimeout(this.toastTimer);
        this.toastTimer = setTimeout(() => { this.toast.style.opacity = 0; }, 3500);
      } else if (!cur) {
        this.lastSettlement = null;
      }
    }

    if (!this.visible || !this.base) return;
    const res = this.base.width;
    const ctx = this.canvas.getContext('2d');
    ctx.drawImage(this.base, 0, 0);
    const half = this.terrain.half;
    const px = ((pos.x + half) / this.terrain.H.mapSize) * res;
    const py = ((half - pos.z) / this.terrain.H.mapSize) * res;
    ctx.save();
    ctx.translate(px, py);
    ctx.rotate(heading ?? 0);
    ctx.fillStyle = '#ff5540';
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(0, -9);
    ctx.lineTo(6, 7);
    ctx.lineTo(-6, 7);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }
}
